import { useState } from "react";
import styled from "@emotion/styled";
import { useNavigate } from "@tanstack/react-router";
import { App, Checkbox } from "antd5";

import { useDownloadCustomerDocument } from "lib/hooks/api/customer_documents/useDownloadCustomerDocument";
import { useTriggerBidDocumentUpload } from "lib/hooks/api/customer_documents/useTriggerBidDocumentUpload";
import CentredSpinner from "../../../lib/core_components/CentredSpinner";
import { BidDetails, useBid } from "../../../lib/hooks/api/bid/bids/useBid";
import {
  CustomerDocumentDetails,
  DEFAULT_CUSTOMER_DOCUMENTS_PAGINATION,
  useCustomerDocuments,
} from "../../../lib/hooks/api/customer_documents/useCustomerDocuments";
import FileTypeIcon from "../../../lib/icons/FileTypeIcon";
import {
  CustomerDocumentSortBy,
  CustomerDocumentSortOrder,
  CustomerDocumentTypeFilter,
  DeleteDocumentType,
} from "../../../lib/types/graphQLEnums";
import { smoothTransition } from "../../../styles/animations";
import { Flex, Text } from "../../../styles/utility-components";
import { DeleteCustomerDocumentModal } from "../../content_library/modals/DeleteCustomerDocumentModal";
import { CustomerDocumentDrawer } from "../../customer_documents/CustomerDocumentDrawer";
import Button from "../../ui/button/Button";
import EmptyState from "../../ui/empty_state/EmptyState";
import Tooltip from "../../ui/tooltip/Tooltip";
import BidDetailsLayout from "../BidDetailsLayout";
import BidDocumentsActionBar from "./BidDocumentsActionBar";
import { UploadTenderDocumentModal } from "./UploadTenderDocumentModal";

type Props = {
  id: string;
};

export default function BidDocumentsList({ id }: Props) {
  const { data: bid, isLoading: isLoadingBid, isError: isErrorBid } = useBid({ id });
  const {
    data: documents,
    isLoading,
    isError,
  } = useCustomerDocuments(
    {
      resourceId: id,
      sortBy: CustomerDocumentSortBy.FileName,
      sortOrder: CustomerDocumentSortOrder.Asc,
      documentType: CustomerDocumentTypeFilter.BidDocument,
    },
    DEFAULT_CUSTOMER_DOCUMENTS_PAGINATION,
  );

  if (isLoading) {
    return <CentredSpinner />;
  }

  if (isError) {
    return (
      <Flex flexGrow={1} alignItems="center" aria-label="Bid documents error state">
        <EmptyState
          type="loadError"
          heading="Sorry something has gone wrong loading these bid documents"
          description="If this error persists please contact support"
          width="100%"
          size="large"
        />
      </Flex>
    );
  }

  if (!documents || documents.length === 0) {
    return (
      <Flex flexGrow={1} alignItems="center">
        <EmptyState
          type="loadError"
          heading="No bid documents found"
          description="No documents have been uploaded for this bid."
          width="100%"
          size="large"
        />
      </Flex>
    );
  }

  return (
    <BidDetailsLayout
      id={id}
      bid={bid}
      isLoading={isLoadingBid}
      isError={isErrorBid}
      sidebarWidth="40%"
    >
      <BidDocumentsListContent id={id} bid={bid} documents={documents} />
    </BidDetailsLayout>
  );
}

function BidDocumentsListContent({
  id,
  bid,
  documents,
}: {
  id: string;
  bid: BidDetails | undefined;
  documents: CustomerDocumentDetails[];
}) {
  const navigate = useNavigate();
  const triggerUpload = useTriggerBidDocumentUpload(id);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState<CustomerDocumentDetails[]>([]);
  const [displayDocument, setDisplayDocument] = useState<CustomerDocumentDetails | null>(null);

  return (
    <>
      <Container>
        <Button
          buttonType="tertiary"
          blackText
          icon="arrowLeft"
          iconProps={{ color: "sysTextDefault", fill: "sysTextDefault" }}
          onClick={() => navigate({ to: `/bids/${id}` })}
        >
          Back
        </Button>
        <Header>
          <Text h1>Tender documents</Text>
          <Button buttonType="secondary" icon="upload01" onClick={() => setIsUploadModalOpen(true)}>
            Upload
          </Button>
        </Header>
        {selectedDocuments.length > 0 && (
          <BidDocumentsActionBar
            id={id}
            selectedDocuments={selectedDocuments}
            onClearSelection={() => setSelectedDocuments([])}
          />
        )}
        <FilesList aria-label="Bid documents list">
          {documents.map((document) => (
            <DocumentItem
              key={document.id}
              document={document}
              showCheckboxes={selectedDocuments.length > 0}
              isSelected={selectedDocuments.some((doc) => doc.id === document.id)}
              onSelectionChange={(checked) =>
                setSelectedDocuments((prev) =>
                  checked ? [...prev, document] : prev.filter((doc) => doc.id !== document.id),
                )
              }
              onDeleteRequest={() => {
                setSelectedDocuments([document]);
                setIsDeleteModalOpen(true);
              }}
              onClick={() => setDisplayDocument(document)}
            />
          ))}
        </FilesList>
      </Container>
      <CustomerDocumentDrawer
        source={{
          documentId: displayDocument?.id ?? "",
          fileName: displayDocument?.fileName ?? "",
          locations: [],
        }}
        onClose={() => setDisplayDocument(null)}
        isOpen={!!displayDocument}
      />
      <UploadTenderDocumentModal
        bidId={id}
        isOpen={isUploadModalOpen}
        onOpenChange={setIsUploadModalOpen}
        sourceUrl={bid?.notice?.url}
        onSubmit={triggerUpload}
      />
      <DeleteCustomerDocumentModal
        selectedDocuments={selectedDocuments}
        isOpen={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        onSuccess={() => setSelectedDocuments([])}
        documentType={DeleteDocumentType.BidDocument}
      />
    </>
  );
}

function DocumentItem({
  document,
  showCheckboxes,
  isSelected,
  onSelectionChange,
  onDeleteRequest,
  onClick,
}: {
  document: CustomerDocumentDetails;
  showCheckboxes: boolean;
  isSelected: boolean;
  onSelectionChange: (checked: boolean) => void;
  onDeleteRequest: () => void;
  onClick: () => void;
}) {
  const { message } = App.useApp();
  const { mutate: downloadDocument } = useDownloadCustomerDocument({
    onError: (error, vars) => {
      console.error(`Error downloading document: ${JSON.stringify(error)}`);
      message.error(`Failed to download document ${vars.fileName}`);
    },
  });

  return (
    <FileContent onClick={onClick} aria-label={document.fileName}>
      <Flex gap={16} alignItems="center">
        <CheckboxWrapper onClick={(e) => e.stopPropagation()} showCheckboxes={showCheckboxes}>
          <Checkbox checked={isSelected} onChange={(e) => onSelectionChange(e.target.checked)} />
        </CheckboxWrapper>
        {/* TODO: Put in specific icons by file extension */}
        <FileTypeIcon type={document.fileName.split(".").pop() ?? "UNK"} />
        <Text ellipsis ellipsisMethod="whiteSpace">
          {document.fileName}
        </Text>
      </Flex>
      <Flex gap={8} alignItems="center">
        <Tooltip content="Download">
          <Button
            buttonType="tertiary"
            aria-label={`Download ${document.fileName}`}
            buttonStyle="inverse"
            icon="download01"
            iconProps={{ color: "sysTextDefault" }}
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
              e.stopPropagation();
              downloadDocument(document);
            }}
          />
        </Tooltip>
        <Tooltip content="Delete">
          <Button
            buttonType="tertiary"
            aria-label={`Delete ${document.fileName}`}
            buttonStyle="inverse"
            icon="trash01"
            iconProps={{
              color: "sysTextDefault",
            }}
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
              e.stopPropagation();
              onDeleteRequest();
            }}
          />
        </Tooltip>
      </Flex>
    </FileContent>
  );
}

const Container = styled(Flex)({
  padding: "64px 42px",
  width: "100%",
  gap: 24,
  alignItems: "flex-start",
  flexDirection: "column",
});

const Header = styled(Flex)({
  paddingLeft: 4,
  paddingRight: 4,
  justifyContent: "space-between",
  width: "100%",
});

const FilesList = styled(Flex)(({ theme }) => ({
  flexDirection: "column",
  width: "100%",
  overflowY: "auto",
  "&::-webkit-scrollbar": {
    width: 7,
    float: "right",
  },
  "&::-webkit-scrollbar-track": {
    boxShadow: "inset 0 0 0px $placeholder-grey",
    borderRadius: 7,
  },

  "&::-webkit-scrollbar-thumb": {
    background: theme.colors.sysTextPlaceholder,
    borderRadius: 7,
  },

  "&::-webkit-scrollbar-thumb:hover": {
    background: theme.colors.sysTextPlaceholder,
  },
}));

const FileContent = styled(Flex)(({ theme }) => ({
  padding: 24,
  borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
  alignItems: "center",
  justifyContent: "space-between",
  cursor: "pointer",

  "&:hover": {
    backgroundColor: theme.colors.sysBackgroundAlternative,
    [`${CheckboxWrapper}`]: {
      opacity: 1,
      pointerEvents: "auto",
    },
  },
}));

const CheckboxWrapper = styled.div<{ showCheckboxes: boolean }>(({ showCheckboxes }) => ({
  opacity: showCheckboxes ? 1 : 0,
  transition: smoothTransition,
  pointerEvents: showCheckboxes ? "auto" : "none", // prevents click when hidden
}));
